import { useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Broadcast } from "@phosphor-icons/react";
import { toast } from "sonner";

const PRESETS = [
  { label: "±3% Either", threshold: 3, direction: "any" },
  { label: "±5% Either", threshold: 5, direction: "any" },
  { label: "Up 3%", threshold: 3, direction: "up" },
  { label: "Down 3%", threshold: 3, direction: "down" },
];

/**
 * Bulk-apply one alert rule to every watchlist row. Includes 4 quick presets
 * for common thresholds so users don't have to type anything for the common case.
 */
export default function BulkAlertButton({ onDone }) {
  const [open, setOpen] = useState(false);
  const [threshold, setThreshold] = useState(3);
  const [direction, setDirection] = useState("any");
  const [busy, setBusy] = useState(false);

  const apply = async (t, d, active = true) => {
    setBusy(true);
    try {
      const { data } = await api.post("/watchlist/alerts/bulk", {
        threshold_pct: Number(t) || 0,
        direction: d,
        active,
      });
      if (active) {
        toast.success(`Applied to ${data.applied} rows`);
      } else {
        toast.success(`Cleared ${data.cleared} alert${data.cleared === 1 ? "" : "s"}`);
      }
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk apply failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="watchlist-bulk-alert-btn"
          variant="outline"
          className="bg-transparent border-zinc-700 hover:bg-zinc-900 rounded-sm mono text-[10px] uppercase tracking-widest gap-1.5 h-9"
        >
          <Broadcast size={12} weight="bold" /> Bulk alerts
        </Button>
      </DialogTrigger>
      <DialogContent
        className="bg-zinc-950 border-zinc-800 max-w-md text-white"
        data-testid="watchlist-bulk-alert-dialog"
      >
        <DialogHeader>
          <DialogTitle className="mono">Bulk alert presets</DialogTitle>
          <DialogDescription className="mono text-xs text-zinc-500">
            Apply one alert rule to every symbol on your watchlist.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest mb-1">Quick presets</div>
            <div className="grid grid-cols-2 gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  onClick={() => apply(p.threshold, p.direction, true)}
                  disabled={busy}
                  data-testid={`bulk-preset-${p.direction}-${p.threshold}`}
                  className="mono text-xs px-3 py-2 rounded-sm border border-zinc-700 hover:border-white hover:bg-zinc-900 disabled:opacity-50 text-left"
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-3">
            <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest mb-1">Custom</div>
            <div className="flex gap-2">
              <Input
                type="number"
                step="0.1"
                min="0.1"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                data-testid="bulk-threshold-input"
                className="bg-zinc-900 border-zinc-800 mono w-20"
              />
              <select
                value={direction}
                onChange={(e) => setDirection(e.target.value)}
                data-testid="bulk-direction-select"
                className="flex-1 bg-zinc-900 border border-zinc-800 rounded-sm mono text-xs h-9 px-2"
              >
                <option value="any">Either direction</option>
                <option value="up">Up only</option>
                <option value="down">Down only</option>
              </select>
              <Button
                onClick={() => apply(threshold, direction, true)}
                disabled={busy}
                data-testid="bulk-apply-btn"
                className="bg-white text-black hover:bg-zinc-200 rounded-sm mono text-[10px] uppercase tracking-widest"
              >
                Apply
              </Button>
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-3">
            <Button
              onClick={() => apply(0, "any", false)}
              disabled={busy}
              data-testid="bulk-clear-btn"
              variant="outline"
              className="w-full border-zinc-700 bg-transparent hover:bg-zinc-900 rounded-sm mono text-[10px] uppercase tracking-widest"
            >
              Clear all alerts
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
